import { motion } from 'framer-motion';
import { Pencil, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { ProductThumbnail } from './ProductThumbnail';
import { sourceMeta } from './sourceMeta';
import { listItemVariants } from './motion';
import { formatBdt, type Product } from '../mockKnowledge';

interface ProductCardProps {
  product: Product;
  onToggleInKnowledgeBase: (id: string) => void;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
}

export function ProductCard({ product, onToggleInKnowledgeBase, onEdit, onDelete }: ProductCardProps) {
  const meta = sourceMeta[product.source] ?? sourceMeta.manual;
  const hasStock = typeof product.stock === 'number' && !Number.isNaN(product.stock);
  const outOfStock = hasStock && product.stock === 0;

  return (
    <motion.div layout variants={listItemVariants} exit="exit">
      <Card className="max-w-none rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 shadow-2xs">
        <div className="flex items-start gap-2.5">
          <ProductThumbnail src={product.imageUrl} alt={product.title} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="truncate text-xs font-bold text-slate-900 dark:text-slate-100">{product.title}</p>
              <span className="shrink-0 text-xs font-black text-slate-900 dark:text-slate-100">{formatBdt(product.price)}</span>
            </div>
            <p className="mt-0.5 truncate text-[10px] text-slate-400 dark:text-slate-500">{product.category}</p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold', meta.badgeClassName)}>
                {meta.name}
              </span>
              <span className={cn('text-[10px] font-medium', outOfStock ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400')}>
                {outOfStock ? 'Out of stock' : hasStock ? `${product.stock} in stock` : 'In stock'}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-2.5 flex items-center justify-between gap-1.5 border-t border-slate-100 dark:border-slate-800 pt-2">
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <Checkbox
              checked={product.inKnowledgeBase}
              onChange={() => onToggleInKnowledgeBase(product.id)}
              aria-label={`Include ${product.title} in AI knowledge`}
            />
            <span className={cn('text-[11px] font-medium', product.inKnowledgeBase ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500')}>
              {product.inKnowledgeBase ? 'In AI Knowledge' : 'Excluded'}
            </span>
          </label>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onEdit(product)}
              aria-label={`Edit ${product.title}`}
              className="rounded-lg p-1.5 text-slate-400 dark:text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(product.id)}
              aria-label={`Delete ${product.title}`}
              className="rounded-lg p-1.5 text-slate-400 dark:text-slate-500 transition-colors hover:bg-rose-50 dark:hover:bg-rose-950/50 hover:text-rose-600 dark:hover:text-rose-400"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export default ProductCard;
